import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2, RefreshCw, ShieldCheck, Store, UserPlus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { KAB_KOTA, kecamatanOptionsOf, kelurahanOptionsOf } from "../lib/wilayah";
import { PhoneInput, isValidPhone, CoordinateInput, isValidCoordinate, MultiSelect } from "../components/UI";
import LocationPicker from "../components/LocationPicker";

const EMPTY_FORM = {
  name: "", email: "", password: "", password_confirm: "", phone: "",
  nama_usaha: "", jenis_produk: [], nama_pic: "", no_hp: "",
  kota: "Medan", kecamatan: "", kelurahan: "", alamat: "",
  lat: 3.5952, lng: 98.6722,
};

export default function RegisterSupplierPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [captcha, setCaptcha] = useState({ captcha_id: "", question: "" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [jenisOptions, setJenisOptions] = useState([]);

  const loadCaptcha = async () => {
    try {
      const { data } = await api.get("/auth/captcha");
      setCaptcha(data);
      setCaptchaAnswer("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    loadCaptcha();
    api.get("/master-data/jenis-produk")
      .then(({ data }) => setJenisOptions(data.map((m) => m.nama)))
      .catch(() => setJenisOptions([]));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onKotaChange = (kota) => {
    const c = KAB_KOTA.find((c) => c[0] === kota);
    const kecs = kecamatanOptionsOf(kota);
    const defaultKec = kecs[0] || "";
    const defaultKel = kelurahanOptionsOf(kota, defaultKec)[0] || "";
    setForm((f) => ({
      ...f,
      kota,
      kecamatan: defaultKec,
      kelurahan: defaultKel,
      lat: c ? c[1] : f.lat,
      lng: c ? c[2] : f.lng,
    }));
  };

  const onKecamatanChange = (kec) => {
    setForm((f) => ({
      ...f,
      kecamatan: kec,
      kelurahan: kelurahanOptionsOf(f.kota, kec)[0] || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password minimal 8 karakter.");
      return;
    }
    if (form.password !== form.password_confirm) {
      setError("Konfirmasi password tidak cocok.");
      return;
    }
    if (form.jenis_produk.length === 0) {
      setError("Pilih minimal satu jenis produk.");
      return;
    }
    if (!isValidPhone(form.no_hp)) {
      setError("No. HP usaha tidak valid. Masukkan minimal 8 angka setelah +62.");
      return;
    }
    if (form.phone && !isValidPhone(form.phone)) {
      setError("No. HP pribadi tidak valid. Masukkan minimal 8 angka setelah +62.");
      return;
    }
    if (!isValidCoordinate(form.lat, "lat") || !isValidCoordinate(form.lng, "lng")) {
      setError("Koordinat tidak valid. Latitude -90 s/d 90, longitude -180 s/d 180.");
      return;
    }
    setLoading(true);
    try {
      const { password_confirm, ...rest } = form;
      await api.post("/auth/register-supplier", {
        ...rest,
        nama_pic: form.nama_pic || form.name,
        phone: form.phone || form.no_hp,
        lat: Number(form.lat),
        lng: Number(form.lng),
        captcha_id: captcha.captcha_id,
        captcha_answer: captchaAnswer,
      });
      setSuccess(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      await loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="register-supplier-success">
        <div className="kedan-card max-w-lg w-full p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-display font-bold text-slate-900 mt-5">Pendaftaran Berhasil</h1>
          <p className="text-slate-600 text-sm mt-3 leading-relaxed">
            Akun supplier <span className="font-medium text-slate-900">{form.email}</span> telah dibuat dengan
            status <span className="font-semibold text-amber-600">Belum Terverifikasi</span>.
            Tim KPPG/Pemda akan meninjau data usaha Anda terlebih dahulu.
            Anda tetap dapat masuk untuk melengkapi profil dan data produk.
          </p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="kedan-btn-primary w-full mt-6 !py-2.5"
            data-testid="register-supplier-goto-login"
          >
            Kembali ke Halaman Masuk
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="register-supplier-page">
      {/* Header bar */}
      <div className="login-bg text-white">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white" data-testid="register-back-to-login">
            <ArrowLeft className="w-4 h-4" /> Kembali ke Masuk
          </Link>
          <div className="flex items-center gap-3 mt-6">
            <div className="w-11 h-11 rounded-md bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase opacity-80">Pemerintah Provinsi Sumatera Utara</div>
              <div className="text-xl font-display font-semibold">KEDAN MBG</div>
            </div>
          </div>
          <h1 className="text-3xl font-display font-bold mt-6">Pendaftaran Akun Supplier</h1>
          <p className="text-white/85 text-sm mt-2 max-w-xl">
            Daftarkan usaha Anda untuk menjadi pemasok pangan. Akun akan berstatus
            belum terverifikasi hingga disetujui oleh KPPG/Pemda.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={submit} className="space-y-6" data-testid="register-supplier-form">
          {/* Data akun */}
          <div className="kedan-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="w-4 h-4 text-[#045498]" />
              <h2 className="font-display font-semibold text-slate-900">Data Akun</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Nama Lengkap</label>
                <input className="kedan-input" required value={form.name}
                  onChange={(e) => set("name", e.target.value)} data-testid="register-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
                <input type="email" className="kedan-input" required value={form.email}
                  onChange={(e) => set("email", e.target.value)} placeholder="nama@usaha.id" data-testid="register-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">No. HP Pribadi</label>
                <PhoneInput value={form.phone} onChange={(v) => set("phone", v)} testid="register-phone" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Password</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} className="kedan-input pr-10" required minLength={8} value={form.password}
                    onChange={(e) => set("password", e.target.value)} placeholder="Minimal 8 karakter" data-testid="register-password" />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                    data-testid="register-password-toggle"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Konfirmasi Password</label>
                <div className="relative">
                  <input type={showPasswordConfirm ? "text" : "password"} className="kedan-input pr-10" required value={form.password_confirm}
                    onChange={(e) => set("password_confirm", e.target.value)} placeholder="Ulangi password" data-testid="register-password-confirm" />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    title={showPasswordConfirm ? "Sembunyikan password" : "Tampilkan password"}
                    data-testid="register-password-confirm-toggle"
                  >
                    {showPasswordConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Data usaha */}
          <div className="kedan-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Store className="w-4 h-4 text-[#045498]" />
              <h2 className="font-display font-semibold text-slate-900">Data Usaha</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Nama Usaha</label>
                <input className="kedan-input" required value={form.nama_usaha}
                  onChange={(e) => set("nama_usaha", e.target.value)} data-testid="register-nama-usaha" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Jenis Produk</label>
                <MultiSelect
                  options={jenisOptions}
                  value={form.jenis_produk}
                  onChange={(v) => set("jenis_produk", v)}
                  placeholder="Pilih satu atau lebih jenis produk"
                  emptyText="Master jenis produk masih kosong. Hubungi admin."
                  testid="register-jenis-produk"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Nama PIC</label>
                <input className="kedan-input" value={form.nama_pic}
                  onChange={(e) => set("nama_pic", e.target.value)} placeholder="Kosongkan jika sama dengan nama lengkap" data-testid="register-nama-pic" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">No. HP Usaha</label>
                <PhoneInput required value={form.no_hp} onChange={(v) => set("no_hp", v)} testid="register-no-hp" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Kabupaten/Kota</label>
                <select className="kedan-input" value={form.kota}
                  onChange={(e) => onKotaChange(e.target.value)} data-testid="register-kota">
                  {KAB_KOTA.map(([c]) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Kecamatan</label>
                <select className="kedan-input" required value={form.kecamatan}
                  onChange={(e) => onKecamatanChange(e.target.value)} data-testid="register-kecamatan">
                  <option value="">Pilih Kecamatan</option>
                  {kecamatanOptionsOf(form.kota).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Kelurahan/Desa</label>
                <select className="kedan-input" required value={form.kelurahan}
                  onChange={(e) => set("kelurahan", e.target.value)} data-testid="register-kelurahan">
                  <option value="">Pilih Kelurahan/Desa</option>
                  {kelurahanOptionsOf(form.kota, form.kecamatan).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Alamat Lengkap</label>
                <textarea rows="2" className="kedan-input" required value={form.alamat}
                  onChange={(e) => set("alamat", e.target.value)} data-testid="register-alamat" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Titik Lokasi Usaha</label>
                <LocationPicker
                  lat={form.lat}
                  lng={form.lng}
                  onChange={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Latitude</label>
                <CoordinateInput axis="lat" value={form.lat} onChange={(v) => set("lat", v)} testid="register-lat" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Longitude</label>
                <CoordinateInput axis="lng" value={form.lng} onChange={(v) => set("lng", v)} testid="register-lng" />
              </div>
            </div>
          </div>

          {/* Verifikasi & submit */}
          <div className="kedan-card p-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Verifikasi Captcha</label>
              <div className="flex gap-2 items-stretch max-w-sm">
                <div className="flex-1 bg-slate-100 border border-slate-200 rounded-md px-4 py-2 flex items-center justify-center font-mono text-base font-semibold text-slate-800 select-none whitespace-nowrap min-h-[42px]"
                  data-testid="register-captcha-question">
                  {captcha.question || "..."}
                </div>
                <input type="text" required value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)}
                  className="kedan-input w-24 text-center font-mono" placeholder="?" data-testid="register-captcha-input" />
                <button type="button" onClick={loadCaptcha} className="kedan-btn-secondary !px-3"
                  title="Refresh captcha" data-testid="register-captcha-refresh">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md px-3 py-2">
              Akun yang didaftarkan berstatus <span className="font-semibold">Belum Terverifikasi</span>.
              Verifikasi dilakukan oleh KPPG/Pemda setelah data usaha diperiksa.
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2" data-testid="register-error">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <Link to="/login" className="kedan-btn-secondary justify-center">Batal</Link>
              <button type="submit" disabled={loading} className="kedan-btn-primary !py-2.5" data-testid="register-submit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                {loading ? "Mendaftarkan..." : "Daftar Sekarang"}
              </button>
            </div>
          </div>
        </form>

        <p className="text-center text-sm text-slate-500 mt-6">
          Sudah punya akun? <Link to="/login" className="text-[#045498] font-medium hover:underline">Masuk di sini</Link>
        </p>
      </div>
    </div>
  );
}
